import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import { getWatchlist, removeWatch, watchKey, WatchItem } from '../services/watchlist.service';
import { getTimeRemaining, formatTimeRemaining } from '../utils/lotTiming';
import { closedLotWord, closedLotStatus } from '../utils/saleState';

/**
 * WATCHING — the bidder's shortlist of lots they intend to bid on but haven't yet.
 * Reuses the My Bids stacking pattern (tile/list toggle persisted to localStorage,
 * clustered cards, per-sale filter). A watched lot the bidder has since bid on
 * "graduates" to My Bids and drops out of this view (kept as data, filtered here).
 */

type ViewMode = 'tile' | 'list';
const VIEW_KEY = 'watching_view';

const money = (cents?: number) => `$${(((cents ?? 0)) / 100).toFixed(2)}`;

interface WatchRow {
  key: string;
  auctionId: string;
  lotId: string;
  lotNumber: string;
  lotTitle: string;
  currentBidCents: number;
  endTime: Date | null;
  exists: boolean; // lot still present on the auction
}

const Watching: React.FC = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const [watched, setWatched] = useState<Record<string, WatchItem>>({});
  const [auctionNames, setAuctionNames] = useState<Record<string, string>>({});
  // Each watched sale's dispute fields (display only) — see closedLotStatus in saleState.ts.
  const [saleDispute, setSaleDispute] = useState<Record<string, { disputeOpen?: boolean; disputedLots?: unknown }>>({});
  const [lotMeta, setLotMeta] = useState<Record<string, { lotNumber: string; title: string; currentBidCents: number; endTime: Date | null; exists: boolean }>>({});
  const [bidKeys, setBidKeys] = useState<Set<string>>(new Set());
  const [selectedAuctionId, setSelectedAuctionId] = useState<string>('all');
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);

  const [viewMode, setViewMode] = useState<ViewMode>(() => (localStorage.getItem(VIEW_KEY) === 'list' ? 'list' : 'tile'));
  const setView = (m: ViewMode) => { setViewMode(m); try { localStorage.setItem(VIEW_KEY, m); } catch { /* ignore */ } };

  // Seconds tick for countdowns.
  useEffect(() => {
    const id = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  const load = async () => {
    if (!currentUser) { setLoading(false); return; }
    setLoading(true);
    try {
      const map = await getWatchlist(currentUser.uid);
      setWatched(map);

      const auctionIds = Array.from(new Set(Object.values(map).map((w) => w.auctionId)));
      const names: Record<string, string> = {};
      const disputeById: Record<string, { disputeOpen?: boolean; disputedLots?: unknown }> = {};
      const meta: typeof lotMeta = {};
      await Promise.all(auctionIds.map(async (id) => {
        try {
          const aSnap = await getDoc(doc(db, 'auctions', id));
          if (!aSnap.exists()) { names[id] = id; return; }
          const data = aSnap.data() as any;
          names[id] = data.title || id;
          disputeById[id] = { disputeOpen: data.disputeOpen, disputedLots: data.disputedLots }; // display only
          (data.lots || []).forEach((l: any) => {
            const endRaw = l.actualEndTime ?? l.scheduledEndTime;
            const endTime = endRaw?.toDate ? endRaw.toDate() : (endRaw ? new Date(endRaw) : null);
            meta[`${id}_${l.id}`] = {
              lotNumber: l.lotNumber,
              title: l.title,
              currentBidCents: l.currentBid ?? l.startingBid ?? 0,
              endTime,
              exists: true,
            };
          });
        } catch { names[id] = id; }
      }));
      setAuctionNames(names);
      setSaleDispute(disputeById);
      setLotMeta(meta);

      // Which watched lots already have this bidder's bid -> they graduated to My Bids.
      const bidsSnap = await getDocs(query(collection(db, 'bids'), where('bidderId', '==', currentUser.uid)));
      const bk = new Set<string>();
      bidsSnap.forEach((d) => { const b = d.data() as any; bk.add(`${b.auctionId}_${b.lotId}`); });
      setBidKeys(bk);
    } catch (e) {
      console.error('Failed to load watching list:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (auctionId: string, lotId: string) => {
    if (!currentUser) return;
    const key = watchKey(auctionId, lotId);
    const prev = watched[key];
    setWatched((w) => { const n = { ...w }; delete n[key]; return n; });
    try {
      await removeWatch(currentUser.uid, auctionId, lotId);
    } catch (e) {
      console.error('Failed to remove watch:', e);
      if (prev) setWatched((w) => ({ ...w, [key]: prev }));
    }
  };

  // Build rows: watched lots the bidder has NOT yet bid on.
  const { rows, graduatedCount } = useMemo(() => {
    void tick; // recompute countdowns each second
    const all: WatchRow[] = [];
    let graduated = 0;
    Object.values(watched)
      .sort((a, b) => (b.addedAt || 0) - (a.addedAt || 0))
      .forEach((w) => {
        const key = watchKey(w.auctionId, w.lotId);
        if (bidKeys.has(key)) { graduated += 1; return; } // now in My Bids
        const m = lotMeta[key];
        all.push({
          key,
          auctionId: w.auctionId,
          lotId: w.lotId,
          lotNumber: m?.lotNumber ?? w.lotNumber ?? '—',
          lotTitle: m?.title ?? w.lotTitle ?? 'Lot',
          currentBidCents: m?.currentBidCents ?? 0,
          endTime: m?.endTime ?? null,
          exists: m?.exists ?? false,
        });
      });
    const filtered = selectedAuctionId === 'all' ? all : all.filter((r) => r.auctionId === selectedAuctionId);
    return { rows: filtered, graduatedCount: graduated };
  }, [watched, bidKeys, lotMeta, selectedAuctionId, tick]);

  const auctionOptions = Array.from(new Set(Object.values(watched).map((w) => w.auctionId)));

  if (!currentUser) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600 mb-4">Please sign in to view your watchlist</p>
        <Link to="/login" className="btn-primary">Sign In</Link>
      </div>
    );
  }
  if (loading) return <div className="text-center py-12"><p className="text-gray-600">Loading your watchlist…</p></div>;

  const listWrap = viewMode === 'list' ? 'space-y-2' : 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3';

  const closedLabel = (r: WatchRow) => {
    const remaining = r.endTime ? getTimeRemaining(new Date(r.endTime)) : 0;
    return remaining <= 0 ? closedLotWord(closedLotStatus(saleDispute[r.auctionId], r), 'lower') : formatTimeRemaining(remaining);
  };

  // ---------- TILE ----------
  const tile = (r: WatchRow) => {
    const remaining = r.endTime ? getTimeRemaining(new Date(r.endTime)) : 0;
    const closed = remaining <= 0;
    return (
      <div key={r.key} className="relative p-4 rounded-lg bg-amber-50 border-2 border-amber-200">
        <button
          type="button"
          aria-label="Remove from watchlist"
          title="Remove from watchlist"
          onClick={(e) => { e.stopPropagation(); handleRemove(r.auctionId, r.lotId); }}
          className="absolute -top-2 -right-2 z-10 bg-white border rounded-full w-6 h-6 flex items-center justify-center text-gray-400 hover:text-gray-700 shadow leading-none"
        >×</button>
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0 pr-6">
            <Link to={`/auctions/${r.auctionId}/lots/${r.lotId}`} className="font-bold text-gray-800 hover:text-primary-600 block">{r.lotTitle}</Link>
            <p className="text-sm text-gray-600">LOT {r.lotNumber}</p>
            {selectedAuctionId === 'all' && <p className="text-xs text-gray-500">{auctionNames[r.auctionId] || r.auctionId}</p>}
            <p className={`text-xs mt-1 font-mono ${closed ? 'text-gray-400' : 'text-amber-700'}`}>{closed ? closedLotWord(closedLotStatus(saleDispute[r.auctionId], r), 'lower') : `closes in ${closedLabel(r)}`}</p>
          </div>
          <div className="text-right flex-shrink-0">
            <p className="text-sm text-gray-600">Current bid</p>
            <p className="text-2xl font-bold text-gray-800">{money(r.currentBidCents)}</p>
            <button
              onClick={() => navigate(`/auctions/${r.auctionId}/lots/${r.lotId}`)}
              className="mt-2 w-full px-3 py-1 text-white rounded text-sm font-semibold bg-primary-600 hover:bg-primary-700"
            >
              {closed ? 'View Lot' : 'Bid Now'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ---------- LIST ----------
  const row = (r: WatchRow) => {
    const remaining = r.endTime ? getTimeRemaining(new Date(r.endTime)) : 0;
    const closed = remaining <= 0;
    return (
      <div key={r.key} className="flex items-center gap-3 py-2 px-3 border rounded border-l-4 border-l-amber-400">
        <span className="text-amber-500 shrink-0">★</span>
        <span className="text-xs font-mono text-gray-500 w-16 shrink-0">LOT {r.lotNumber}</span>
        <Link to={`/auctions/${r.auctionId}/lots/${r.lotId}`} className="flex-1 min-w-0 truncate font-semibold text-gray-800 hover:text-primary-600">{r.lotTitle}</Link>
        <span className={`hidden sm:inline w-24 text-right text-xs font-mono shrink-0 ${closed ? 'text-gray-400' : 'text-amber-700'}`}>{closedLabel(r)}</span>
        <span className="w-20 text-right font-bold text-gray-800 shrink-0">{money(r.currentBidCents)}</span>
        <button onClick={() => navigate(`/auctions/${r.auctionId}/lots/${r.lotId}`)} className="text-xs font-semibold px-3 py-1 rounded text-white bg-primary-600 hover:bg-primary-700 shrink-0">{closed ? 'View' : 'Bid'}</button>
        <button type="button" aria-label="Remove from watchlist" title="Remove" onClick={(e) => { e.stopPropagation(); handleRemove(r.auctionId, r.lotId); }} className="w-8 text-center text-gray-400 hover:text-gray-700 text-lg leading-none shrink-0">×</button>
      </div>
    );
  };

  const item = (r: WatchRow) => (viewMode === 'list' ? row(r) : tile(r));

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-3xl font-bold">👀 Watching</h1>
        <div className="flex items-center gap-3">
          <div className="inline-flex rounded border overflow-hidden">
            <button onClick={() => setView('tile')} className={`px-3 py-2 text-sm font-semibold ${viewMode === 'tile' ? 'bg-primary-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>Tiles</button>
            <button onClick={() => setView('list')} className={`px-3 py-2 text-sm font-semibold border-l ${viewMode === 'list' ? 'bg-primary-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>List</button>
          </div>
          {auctionOptions.length > 1 && (
            <select value={selectedAuctionId} onChange={(e) => setSelectedAuctionId(e.target.value)} className="input py-2" aria-label="Filter by sale">
              <option value="all">All sales</option>
              {auctionOptions.map((id) => (<option key={id} value={id}>{auctionNames[id] || id}</option>))}
            </select>
          )}
          <Link to="/browse" className="btn-secondary">Browse Auctions</Link>
        </div>
      </div>

      <p className="text-sm text-gray-600">
        Your shortlist of lots to bid on. Tap <span className="font-semibold text-amber-700">Bid Now</span> when you're ready — once you bid, a lot moves to <Link to="/my-bids" className="text-primary-600 hover:underline">My Bids</Link>.
      </p>

      {graduatedCount > 0 && (
        <p className="text-sm text-green-700 bg-green-50 border border-green-200 rounded px-3 py-2">
          ✓ {graduatedCount} lot{graduatedCount === 1 ? '' : 's'} you were watching {graduatedCount === 1 ? 'is' : 'are'} now in <Link to="/my-bids" className="underline font-semibold">My Bids</Link>.
        </p>
      )}

      {rows.length > 0 ? (
        <div className="card">
          <h2 className="text-xl font-bold mb-4 text-amber-700 flex items-center gap-2">★ Watching ({rows.length})</h2>
          <div className={listWrap}>{rows.map((r) => item(r))}</div>
        </div>
      ) : (
        <div className="card text-center py-12">
          <p className="text-gray-600 mb-4">
            {Object.keys(watched).length === 0
              ? "You're not watching any lots yet."
              : graduatedCount > 0
                ? 'All your watched lots are now in My Bids.'
                : 'No watched lots for this sale.'}
          </p>
          <p className="text-sm text-gray-500 mb-4">Tap the ☆ on any lot to add it to your watchlist.</p>
          <Link to="/browse" className="btn-primary">Browse Auctions</Link>
        </div>
      )}
    </div>
  );
};

export default Watching;
